import { useEffect } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";
import { useSession } from "@/components/SessionProvider";
import {
  redeemFriendUrl,
  takePendingFriendToken,
  takePendingInviteCode,
} from "@/lib/friendInvite";
import { deepLinkRoute } from "@/lib/deepLinkRoute";

/**
 * b14: handle the nexusdex://add-friend?handle=&token= deep link, plus the
 * viral-engine invite universal link (nexusdex.ai/friends/redeem?token=CODE).
 *
 * When a trainer scans another trainer's QR code (or taps a shared invite
 * link), the OS hands the URL to Capacitor's `appUrlOpen` event. We verify the
 * scanner is signed in, POST the signed token to /api/friends/qr-redeem so the
 * request is auto-created, then route them to the Friends tab with a toast.
 *
 * 2026-10-26: page deep links too. App Store in-app event cards carry
 * `nexusdex://calendar` (or the universal link nexusdex.ai/calendar) and the
 * app has to land on the Calendar for Apple to accept the link.
 * lib/deepLinkRoute maps those URLs to a path; anything it does not know falls
 * through to the friend-invite redemption, unchanged.
 *
 * Mounted inside <Router> so it can navigate. No-op outside a Capacitor
 * runtime (the import simply fails and we bail).
 */

/**
 * The last URL this module routed to a page. getLaunchUrl() hands back the
 * most recent URL the OS opened us with (not only the cold-start one), and the
 * effect below re-runs whenever `account` changes identity, which it does on
 * every /me refresh. Re-redeeming a friend token is harmless (the server
 * answers "already friends"); re-routing would pull the trainer back to the
 * Calendar mid-session. So a URL read back from getLaunchUrl routes once.
 */
let lastRoutedUrl: string | null = null;

export function DeepLinkHandler() {
  const { toast } = useToast();
  const { account } = useAuth();
  const { openPaywall, refreshEntitlements } = useSession();
  const [, setLocation] = useLocation();

  useEffect(() => {
    let remove: (() => void) | undefined;
    let cancelled = false;

    const redeem = (url: string) =>
      redeemFriendUrl(url, {
        isSignedIn: !!account,
        toast,
        navigate: setLocation,
        onFriendLimit: (message) => openPaywall("deep-link", message),
        // A code redeem can pay the redeemer's 7-day Pro grant: re-pull
        // entitlements so the gates unlock now, matching the Code tab.
        onProGranted: () => void refreshEntitlements(),
      });

    // Page deep links (App Store event cards: nexusdex://calendar) go straight
    // to their page; everything else is a friend invite, handled as before.
    const open = (url: string, source: "event" | "launch") => {
      const path = deepLinkRoute(url);
      if (!path) {
        void redeem(url);
        return;
      }
      if (source === "launch" && lastRoutedUrl === url) return;
      lastRoutedUrl = url;
      setLocation(path);
    };

    void (async () => {
      try {
        const { App } = await import("@capacitor/app" as any);
        const handle = await App.addListener(
          "appUrlOpen",
          (event: { url: string }) => {
            if (cancelled || !event?.url) return;
            open(event.url, "event");
          },
        );
        remove = () => { void handle.remove(); };

        // Cold-start: the app may have been launched FROM the deep link, in
        // which case the event already fired before this listener attached.
        const launch = await App.getLaunchUrl().catch(() => null);
        if (launch?.url) open(launch.url, "launch");
      } catch {
        /* not a Capacitor runtime; nothing to listen to */
      }
    })();

    // Once signed in, redeem any token or invite code captured while logged out.
    if (account) {
      const pending = takePendingFriendToken();
      if (pending) {
        void redeem(`add-friend?token=${encodeURIComponent(pending)}`);
      }
      const pendingCode = takePendingInviteCode();
      if (pendingCode) {
        void redeem(`friends/redeem?token=${encodeURIComponent(pendingCode)}`);
      }
    }

    return () => {
      cancelled = true;
      remove?.();
    };
  }, [account, setLocation, toast]);

  return null;
}
